import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

/**
 * M08-005 §11 — Vendor clarification register.
 *
 * Lists clarification threads (GET /v1/vendor/clarifications) with a status
 * filter and lets an authorised user answer an OPEN thread
 * (POST /v1/vendor/clarifications/:id/reply). New threads are raised from the
 * job-detail screen (see VendorJobClarifyPage).
 */
interface ClarificationRow {
  readonly clarificationId: string;
  readonly jobRequestId: string;
  readonly vendorAssignmentId: string;
  readonly vendorId: string;
  readonly subject: string;
  readonly questionText: string;
  readonly raisedBy: string;
  readonly raisedAt: string;
  readonly repliedBy: string | null;
  readonly repliedAt: string | null;
  readonly replyText: string | null;
  readonly status: 'OPEN' | 'ANSWERED';
}

interface ThreadResponse {
  readonly rows: ClarificationRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

const STATUSES: Array<'' | 'OPEN' | 'ANSWERED'> = ['', 'OPEN', 'ANSWERED'];

export function VendorClarificationsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<ThreadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'' | 'OPEN' | 'ANSWERED'>('');
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('limit', '50');
      const res = await apiRequest<ThreadResponse>(`/v1/vendor/clarifications?${params.toString()}`, { token });
      setData(res);
    } catch (e) {
      setError((e as Error).message);
      setData({ rows: [], total: 0, limit: 50, offset: 0 });
    }
  }, [token, status]);

  useEffect(() => { void load(); }, [load]);

  async function submitReply(clarificationId: string) {
    setReplyError(null);
    if (!replyText.trim()) {
      setReplyError(t('vops.clarifications.replyRequired'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest(`/v1/vendor/clarifications/${clarificationId}/reply`, {
        method: 'POST',
        token,
        body: { replyText: replyText.trim() },
      });
      setReplyFor(null);
      setReplyText('');
      await load();
    } catch (e) {
      setReplyError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="vendor-clarifications">
      <h1>{t('vops.clarifications.title')}</h1>

      <div>
        <label htmlFor="clarify-status">{t('vops.common.status')}</label>
        <select
          id="clarify-status"
          aria-label="clarify-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | 'OPEN' | 'ANSWERED')}
        >
          {STATUSES.map((s) => <option key={s || 'ALL'} value={s}>{s || t('vops.common.all')}</option>)}
        </select>
      </div>

      {error ? <p role="alert">{error}</p> : null}

      {!data ? (
        <p>{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p aria-label="clarifications-empty">{t('vops.clarifications.empty')}</p>
      ) : (
        <ul aria-label="clarifications-list">
          {data.rows.map((c) => (
            <li key={c.clarificationId} aria-label={`clarification-${c.clarificationId}`}>
              <strong>{c.subject}</strong>{' '}
              <span aria-label={`clarify-state-${c.clarificationId}`}>{c.status}</span>
              <p aria-label={`clarify-question-${c.clarificationId}`}>{t('vops.clarifications.question', { text: c.questionText })}</p>
              {c.replyText ? (
                <p aria-label={`clarify-reply-${c.clarificationId}`}>{t('vops.clarifications.answer', { text: c.replyText })}</p>
              ) : replyFor === c.clarificationId ? (
                <div>
                  <label htmlFor={`reply-${c.clarificationId}`}>{t('vops.clarifications.reply')}</label>
                  <textarea
                    id={`reply-${c.clarificationId}`}
                    aria-label={`reply-input-${c.clarificationId}`}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={busy}
                  />
                  {replyError ? <p role="alert">{replyError}</p> : null}
                  <Button
                    type="button"
                    aria-label={`reply-submit-${c.clarificationId}`}
                    onClick={() => submitReply(c.clarificationId)}
                    disabled={busy}
                    variant="secondary"
                  >
                    {t('vops.clarifications.sendReply')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  aria-label={`reply-open-${c.clarificationId}`}
                  onClick={() => { setReplyFor(c.clarificationId); setReplyText(''); setReplyError(null); }}
                  variant="secondary"
                >
                  {t('vops.clarifications.reply')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
